/**
 * useHoverGlowInjection — applies the site-wide "hover-glow" + "link underline
 * animation" CSS layer to the canvas + live site based on the theme toggles
 * stored on `siteTheme.hover_glow` / `siteTheme.link_underline_anim`.
 *
 * Iteration 138 — Phase B "Universal hover-glow polish layer".
 */
import { useEffect } from 'react';

const STYLE_ID = 'wb-hover-glow-layer';

export function useHoverGlowInjection(siteTheme) {
  const enableGlow      = !!(siteTheme && siteTheme.hover_glow);
  const enableUnderline = !!(siteTheme && siteTheme.link_underline_anim);
  const accent = (siteTheme && (siteTheme.accent_color || (Array.isArray(siteTheme.swatches) && siteTheme.swatches[3]))) || '#A78BFA';

  useEffect(() => {
    if (!enableGlow && !enableUnderline) {
      const existing = document.getElementById(STYLE_ID);
      if (existing) existing.remove();
      return;
    }
    let el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      document.head.appendChild(el);
    }
    // Iter 182 — Scope hover-glow ONLY to the published live site
    // (.wb-site-root). Previously this also targeted the editor canvas
    // (`[data-testid="content-editor"]`), which fired a 350ms box-shadow
    // fade-in/out on every native :hover toggle of any button/link.
    // Since the cursor naturally jitters across element boundaries while
    // authoring, the glow pulsed at 5-10 Hz — exactly the flicker the
    // user reported in their 8th video. The published live site is
    // unaffected (the glow still fires correctly there).
    const scope = '.wb-site-root';
    const glow = enableGlow ? `
      ${scope} a[class*="bg-"]:not(.no-glow):hover,
      ${scope} button[class*="bg-"]:not(.no-glow):hover,
      ${scope} .wb-glow-target:hover {
        box-shadow: 0 0 0 0 ${accent}66, 0 12px 28px -8px ${accent}80 !important;
        transform: translateY(-1px);
        transition: box-shadow .35s ease, transform .35s ease;
      }
    ` : '';
    const underline = enableUnderline ? `
      ${scope} a:not([class*="bg-"]):not(.no-underline-anim) {
        position: relative;
        background-image: linear-gradient(${accent}, ${accent});
        background-position: 0 100%;
        background-repeat: no-repeat;
        background-size: 0% 1.5px;
        transition: background-size .35s ease;
      }
      ${scope} a:not([class*="bg-"]):not(.no-underline-anim):hover {
        background-size: 100% 1.5px;
      }
    ` : '';
    el.textContent = glow + underline;
    return () => {};
  }, [enableGlow, enableUnderline, accent]);
}
